"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import { m, AnimatePresence } from "framer-motion"
import { useLocale } from "next-intl"
import { useRouter } from "next/navigation"
import { cn } from "@/lib/utils"
import {
  localeLabels,
  nativeLocaleLabels,
  getCountryConfigStatic,
  type Country,
  type CountryConfig,
} from "@/i18n/config"
import { track } from "@/lib/analytics"
import { useCountry } from "@/components/layout/country-provider"
import { usePickerCountries } from "@/lib/use-preview-mode"
import { gateForPath, isAllowedInCountry } from "@/lib/country-gates"
import { canonicalSlugPath, relocalizePath } from "@/lib/localized-route-slugs"

/**
 * Build the URL to navigate to when the visitor switches country/locale.
 *
 * Normally `/{targetCountry}/{targetLocale}{localizedPath}`. Two wrinkles:
 *
 * 1. **Localized route slugs** (PR #2356). A page's public URL is translated
 *    per locale — `/ch/de/versicherung`, `/ch/fr/assurance` for the one English
 *    `/insurance` route. So we can't paste the current literal slug after the
 *    new locale prefix: from `/ch/fr/assurance`, switching to DE must yield
 *    `/ch/de/versicherung`, not `/ch/de/assurance` (which serves the "Not found"
 *    page). We canonicalize the incoming slug back to English, then re-localize
 *    it into the target locale (`relocalizePath`), landing on a real 200 with no
 *    redirect hop.
 * 2. **Country gate.** When the current page is country-gated and the target
 *    country doesn't allow it, the URL would 404 — so fall back to the target
 *    country's home. The gate keys off the *canonical* slug, so it's resolved
 *    from `canonicalSlugPath(currentPath)`, not the localized form.
 *
 * `currentPath` is the path AFTER the `/{country}/{locale}` prefix, with
 * a leading slash (e.g. "/insurance", "/assurance", "/pricing", or "" for the
 * home). Callers pass `window.location.pathname`, so it carries no query/hash;
 * `relocalizePath` preserves them regardless.
 */
export function buildLocaleSwitchUrl({
  currentPath,
  targetCountry,
  targetLocale,
}: {
  currentPath: string
  targetCountry: Country
  targetLocale: string
}): string {
  const canonicalPath = canonicalSlugPath(currentPath)
  const gate = gateForPath(canonicalPath)
  if (gate && !isAllowedInCountry(gate, targetCountry)) {
    const defaultLocale = getCountryConfigStatic(targetCountry)!.defaultLanguage
    // Country-home is /{country}/{locale} without trailing slash — matches
    // locale-suggestion.tsx and useLocalePath. skipTrailingSlashRedirect: true
    // in next.config.ts means /nl/nl/ and /nl/nl are distinct Next.js URLs.
    return `/${targetCountry}/${defaultLocale}`
  }
  return `/${targetCountry}/${targetLocale}${relocalizePath(currentPath, targetLocale)}`
}

/** Swiss national flag — square 1:1 ratio (one of two square sovereign flags) */
function SwissFlag({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 16 16"
      className={cn("shrink-0 overflow-hidden rounded-[2px]", className)}
      aria-hidden="true"
    >
      <rect width="16" height="16" fill="#D52B1E" />
      <rect x="6.5" y="3" width="3" height="10" rx="0.4" fill="#fff" />
      <rect x="3" y="6.5" width="10" height="3" rx="0.4" fill="#fff" />
    </svg>
  )
}

function DutchFlag({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 16 11"
      className={cn("shrink-0 overflow-hidden rounded-[2px]", className)}
      aria-hidden="true"
    >
      <rect width="16" height="3.67" fill="#AE1C28" />
      <rect y="3.67" width="16" height="3.67" fill="#FFF" />
      <rect y="7.33" width="16" height="3.67" fill="#21468B" />
    </svg>
  )
}

function GermanFlag({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 16 10"
      className={cn("shrink-0 overflow-hidden rounded-[2px]", className)}
      aria-hidden="true"
    >
      <rect width="16" height="3.33" y="0" fill="#000000" />
      <rect width="16" height="3.33" y="3.33" fill="#DD0000" />
      <rect width="16" height="3.33" y="6.66" fill="#FFCE00" />
    </svg>
  )
}

function AustrianFlag({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 16 11"
      className={cn("shrink-0 overflow-hidden rounded-[2px]", className)}
      aria-hidden="true"
    >
      <rect width="16" height="3.67" fill="#ED2939" />
      <rect y="3.67" width="16" height="3.67" fill="#FFF" />
      <rect y="7.33" width="16" height="3.67" fill="#ED2939" />
    </svg>
  )
}

export function CountryFlag({
  flag,
  className,
}: {
  flag: "ch" | "nl" | "de" | "at"
  className?: string
}) {
  if (flag === "nl") return <DutchFlag className={className} />
  if (flag === "de") return <GermanFlag className={className} />
  if (flag === "at") return <AustrianFlag className={className} />
  return <SwissFlag className={className} />
}

/**
 * Localized country-name resolver. `Intl.DisplayNames` turns the ISO-3166
 * region code (ch/nl/de) into the country name in the current UI locale —
 * "Switzerland" on /ch/en, "Schweiz" on /ch/de, "Suisse" on /ch/fr — with
 * no translation files to maintain. Memoized per locale; falls back to the
 * uppercase code if the runtime lacks `Intl.DisplayNames` (it never should).
 */
function useCountryName(locale: string): (code: string) => string {
  return useMemo(() => {
    let display: Intl.DisplayNames | null = null
    try {
      display = new Intl.DisplayNames([locale], { type: "region" })
    } catch {
      display = null
    }
    return (code: string) =>
      display?.of(code.toUpperCase()) ?? code.toUpperCase()
  }, [locale])
}

/**
 * The shared picker body: every region on its own row (flag + localized
 * country name) with that region's languages — shown as their native names
 * — directly tappable beside it. No "pick country, then pick language"
 * step; one tap commits. The current locale is highlighted.
 *
 * Flags mark the *region* only (decorative, `aria-hidden`). Each language
 * button carries an `aria-label` ("Switzerland — Deutsch") so screen-reader
 * users get full country + language context regardless of the visible label.
 *
 * `layout="inline"` (desktop) lays languages out beside the region name;
 * `layout="stacked"` (mobile) wraps them below it with larger tap targets.
 */
function LocaleGrid({
  countries,
  currentCountry,
  currentLocale,
  onSelect,
  layout = "inline",
}: {
  countries: CountryConfig[]
  currentCountry: Country
  currentLocale: string
  onSelect: (country: Country, lang: string) => void
  layout?: "inline" | "stacked"
}) {
  const countryName = useCountryName(currentLocale)
  return (
    <div role="menu" className="flex flex-col">
      {countries.map((cc, i) => (
        <div
          key={cc.code}
          className={cn(
            "px-3 py-2.5",
            i > 0 && "border-t border-neutral-100",
            layout === "inline"
              ? "grid grid-cols-[7.5rem_1fr] items-center gap-3"
              : "flex flex-col items-stretch gap-2"
          )}
        >
          <span className="type-body-md flex items-center gap-2 whitespace-nowrap text-neutral-900">
            <CountryFlag flag={cc.flag} className="w-[18px]" />
            {countryName(cc.code)}
          </span>
          <span
            className={cn(
              "flex",
              // Inline: one row per region, never wrap (panel grows to fit).
              // Stacked: chips wrap below the region name in a fixed-width sheet.
              layout === "inline" ? "flex-nowrap gap-1" : "flex-wrap gap-2"
            )}
          >
            {cc.languages.map((lang) => {
              const isCurrent =
                cc.code === currentCountry && lang === currentLocale
              return (
                <button
                  key={lang}
                  type="button"
                  role="menuitemradio"
                  aria-checked={isCurrent}
                  aria-label={`${countryName(cc.code)} — ${nativeLocaleLabels[lang] ?? lang}`}
                  onClick={() => onSelect(cc.code, lang)}
                  className={cn(
                    "type-body-md cursor-pointer rounded-full transition-colors",
                    layout === "inline" ? "px-3 py-1.5" : "px-4 py-2",
                    isCurrent
                      ? "bg-aubergine-900 text-white"
                      : cn(
                          "hover:bg-aubergine-50 hover:text-aubergine-900 text-neutral-600",
                          layout === "stacked" && "bg-neutral-100"
                        )
                  )}
                >
                  {nativeLocaleLabels[lang] ?? lang}
                </button>
              )
            })}
          </span>
        </div>
      ))}
    </div>
  )
}

/**
 * Whether to show the multi-country region grid. The grid only earns its
 * complexity (flags, localized country names, per-region rows) once there's
 * a market beyond Switzerland to switch to — i.e. NL, DE, or AT is LAUNCHED (in
 * `publicCountries`) or the visitor holds an `nl-preview` / `de-preview` /
 * `at-preview` cookie (which `usePickerCountries` folds in client-side).
 *
 * When only CH is in play (the public default until launch) each surface
 * falls back to its **legacy** picker — the exact flat language switcher the
 * site shipped before this redesign — so Swiss visitors see no change at all
 * until we go live in another country. The whole region-grid feature stays
 * dormant in production until then.
 */
export function shouldShowRegionGrid(countries: CountryConfig[]): boolean {
  return countries.some((cc) => cc.code !== "ch")
}

interface DesktopLocalePickerProps {
  localeOpen: boolean
  setLocaleOpen: (open: boolean) => void
  switchLocale: (country: string, language: string) => void
  isTransparent: boolean
}

export function DesktopLocalePicker({
  localeOpen,
  setLocaleOpen,
  switchLocale,
  isTransparent,
}: DesktopLocalePickerProps) {
  const locale = useLocale()
  const countryConfig = useCountry()
  const pickerCountries = usePickerCountries(countryConfig.code)
  const showRegionGrid = shouldShowRegionGrid(pickerCountries)

  // Hover-intent: open immediately, but close on a short grace delay so the
  // panel doesn't vanish the instant the pointer overshoots the trigger or
  // dips through the gap on its way down to the menu. Re-entering within the
  // window cancels the pending close.
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }
  const openNow = () => {
    cancelClose()
    setLocaleOpen(true)
  }
  const closeSoon = () => {
    cancelClose()
    closeTimer.current = setTimeout(() => setLocaleOpen(false), 150)
  }
  useEffect(() => cancelClose, [])

  function handleSelect(country: Country, lang: string) {
    if (country === countryConfig.code && lang === locale) {
      setLocaleOpen(false)
      return
    }
    track.localeChanged({
      surface: "navbar-desktop",
      from_country: countryConfig.code,
      from_locale: locale,
      to_country: country,
      to_locale: lang,
      page_path: window.location.pathname,
    })
    switchLocale(country, lang)
  }

  // CH-only fallback — the exact pre-redesign picker (expanding panel + flat
  // language list). Kept byte-for-byte so Swiss visitors see no change in
  // production until NL/DE launches and `showRegionGrid` flips on.
  if (!showRegionGrid) {
    return (
      <div
        className="relative hidden md:block"
        onMouseEnter={openNow}
        onMouseLeave={closeSoon}
      >
        {/* Trigger — always visible. Hover opens it for desktop pointer users;
         * onClick toggles it so touch / keyboard / browser-test agents can
         * activate it too without falling onto the next focusable element. */}
        <button
          type="button"
          aria-haspopup="menu"
          aria-expanded={localeOpen}
          onClick={() => setLocaleOpen(!localeOpen)}
          className={cn(
            "type-body-md relative z-10 inline-flex cursor-pointer items-center gap-1.5 px-3 py-1.5 transition-colors duration-200",
            localeOpen
              ? "text-aubergine-900"
              : isTransparent
                ? "text-white/90 hover:text-white"
                : "hover:text-aubergine-900 text-foreground"
          )}
        >
          <CountryFlag
            flag={countryConfig.flag}
            className={cn(
              "w-[14px] transition-opacity duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]",
              localeOpen ? "opacity-100" : "opacity-0"
            )}
          />
          {localeLabels[locale] || "EN"}
          <svg
            width="10"
            height="10"
            viewBox="0 0 12 12"
            fill="none"
            className={cn(
              "transition-transform duration-200",
              localeOpen && "rotate-180"
            )}
          >
            <path
              d="M3 4.5L6 7.5L9 4.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* Expanding panel — positioned absolutely, starts from top of trigger */}
        {/* safe-animate-presence: conditional-mount — child only renders when `localeOpen=true` */}
        <AnimatePresence>
          {localeOpen && (
            <m.div
              initial={{ height: 34, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 34, opacity: 0 }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
              className="absolute top-0 right-0 z-[5] min-w-full overflow-hidden rounded-[14px] bg-white shadow-lg"
            >
              {/* Invisible spacer — matches trigger height so options start below it */}
              <div
                className="type-body-md invisible flex items-center gap-1.5 px-3 py-1.5"
                aria-hidden="true"
              >
                <CountryFlag flag={countryConfig.flag} className="w-[14px]" />
                {localeLabels[locale] || "EN"}
              </div>

              {/* All other locales — flags distinguish countries. Defaults
               *  to `publicCountries` (LAUNCHED markets only) for SSR and
               *  Googlebot; `usePickerCountries` widens to include the
               *  visitor's preview-cookie markets + current country after
               *  hydration so previewers can find their way back. */}
              {pickerCountries
                .flatMap((cc) =>
                  cc.languages
                    .filter(
                      (lang) =>
                        !(cc.code === countryConfig.code && lang === locale)
                    )
                    .map((lang) => ({ country: cc, lang }))
                )
                .map(({ country: cc, lang }, i) => (
                  <m.button
                    key={`${cc.code}-${lang}`}
                    type="button"
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.2,
                      delay: i * 0.05,
                      ease: [0.16, 1, 0.3, 1],
                    }}
                    onClick={() => {
                      track.localeChanged({
                        surface: "navbar-desktop",
                        from_country: countryConfig.code,
                        from_locale: locale,
                        to_country: cc.code,
                        to_locale: lang,
                        page_path: window.location.pathname,
                      })
                      switchLocale(cc.code, lang)
                    }}
                    className="type-body-md hover:text-aubergine-900 flex w-full cursor-pointer items-center gap-1.5 px-3 py-1.5 text-left text-neutral-400 transition-colors"
                  >
                    <CountryFlag flag={cc.flag} className="w-[14px]" />
                    {localeLabels[lang]}
                  </m.button>
                ))}
              <div className="h-1" />
            </m.div>
          )}
        </AnimatePresence>
      </div>
    )
  }

  return (
    <div
      className="relative hidden md:block"
      onMouseEnter={openNow}
      onMouseLeave={closeSoon}
    >
      {/* Trigger — always visible. Hover opens it for desktop pointer users;
       * onClick toggles it so touch / keyboard / browser-test agents can
       * activate it too without falling onto the next focusable element. */}
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={localeOpen}
        onClick={() => setLocaleOpen(!localeOpen)}
        className={cn(
          "type-body-md relative z-10 inline-flex cursor-pointer items-center gap-1.5 px-3 py-1.5 transition-colors duration-200",
          // On the transparent hero navbar, stay white even while open —
          // hovering the trigger opens the panel, and the trigger sits on the
          // photo, not the white panel, so aubergine would read as wrong.
          isTransparent
            ? "text-white/90 hover:text-white"
            : localeOpen
              ? "text-aubergine-900"
              : "hover:text-aubergine-900 text-foreground"
        )}
      >
        <CountryFlag
          flag={countryConfig.flag}
          className={cn(
            "w-[14px] transition-opacity duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]",
            localeOpen ? "opacity-100" : "opacity-0"
          )}
        />
        {localeLabels[locale] || "EN"}
        <svg
          width="10"
          height="10"
          viewBox="0 0 12 12"
          fill="none"
          aria-hidden="true"
          className={cn(
            "transition-transform duration-200",
            localeOpen && "rotate-180"
          )}
        >
          <path
            d="M3 4.5L6 7.5L9 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* Drops below the trigger. safe-animate-presence: conditional-mount —
       * child only renders when `localeOpen=true`. */}
      <AnimatePresence>
        {localeOpen && (
          <m.div
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            style={{ transformOrigin: "top right" }}
            // `top-full` (touching the trigger) + a transparent `pt-2` bridge
            // keeps the 8px visual gap while making the hover region continuous,
            // so the pointer can travel trigger → menu without crossing a dead
            // zone that would close it.
            className="absolute top-full right-0 z-[5] w-max pt-2"
          >
            <div className="w-max rounded-[16px] bg-white p-1.5 shadow-lg">
              <LocaleGrid
                countries={pickerCountries}
                currentCountry={countryConfig.code}
                currentLocale={locale}
                onSelect={handleSelect}
                layout="inline"
              />
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </div>
  )
}

/** Compact locale picker for the footer — rendered on mobile only
 *  (PageShell wraps it in `md:hidden`); desktop uses DesktopLocalePicker
 *  in the navbar. */
export function FooterLocalePicker() {
  const locale = useLocale()
  const countryConfig = useCountry()
  const pickerCountries = usePickerCountries(countryConfig.code)
  const showRegionGrid = shouldShowRegionGrid(pickerCountries)
  const router = useRouter()
  const [open, setOpen] = useState(false)

  function handleSelect(newCountry: Country, newLanguage: string) {
    if (newCountry === countryConfig.code && newLanguage === locale) {
      setOpen(false)
      return
    }
    const segments = window.location.pathname.split("/").filter(Boolean)
    const pagePath = segments.slice(2).join("/")
    const currentPath = pagePath ? `/${pagePath}` : ""
    const newPath = buildLocaleSwitchUrl({
      currentPath,
      targetCountry: newCountry,
      targetLocale: newLanguage,
    })
    track.localeChanged({
      surface: "footer",
      from_country: countryConfig.code,
      from_locale: locale,
      to_country: newCountry,
      to_locale: newLanguage,
      page_path: window.location.pathname,
    })
    router.push(newPath, { scroll: false })
    document.cookie = `preferred-language=${newLanguage};path=/;max-age=${60 * 60 * 24 * 365};SameSite=Lax`
    setOpen(false)
  }

  // CH-only fallback — the exact pre-redesign footer picker (compact flat
  // list). Byte-for-byte identical to today so nothing changes in production
  // until NL/DE launches.
  if (!showRegionGrid) {
    const otherLocales = pickerCountries.flatMap((cc) =>
      cc.languages
        .filter((lang) => !(cc.code === countryConfig.code && lang === locale))
        .map((lang) => ({ country: cc, lang }))
    )
    return (
      // Stack above the floating chat launcher (z-index 9998). Without this,
      // the fixed-position launcher overlaps the picker in the bottom of the
      // page footer and intercepts taps on both the trigger and the dropdown.
      <div className="relative z-[9999]">
        <button
          type="button"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="hover:text-aubergine-600 inline-flex cursor-pointer items-center gap-1.5 text-sm text-neutral-600 transition-colors"
        >
          <CountryFlag flag={countryConfig.flag} className="w-[14px]" />
          {localeLabels[locale] || "EN"}
          <svg
            width="10"
            height="10"
            viewBox="0 0 12 12"
            fill="none"
            aria-hidden="true"
            className={cn(
              "transition-transform duration-200",
              open && "rotate-180"
            )}
          >
            <path
              d="M3 4.5L6 7.5L9 4.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        {/* safe-animate-presence: conditional-mount — child only renders when `open=true` */}
        <AnimatePresence>
          {open && (
            <m.div
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 4 }}
              transition={{ duration: 0.15 }}
              className="absolute bottom-full left-0 z-10 mb-1 min-w-[120px] overflow-hidden rounded-[12px] bg-white shadow-lg"
            >
              {otherLocales.map(({ country: cc, lang }) => (
                <button
                  key={`${cc.code}-${lang}`}
                  type="button"
                  onClick={() => handleSelect(cc.code, lang)}
                  className="hover:text-aubergine-900 flex w-full cursor-pointer items-center gap-1.5 px-3 py-2 text-left text-sm text-neutral-600 transition-colors hover:bg-neutral-50"
                >
                  <CountryFlag flag={cc.flag} className="w-[14px]" />
                  {localeLabels[lang]}
                </button>
              ))}
            </m.div>
          )}
        </AnimatePresence>
      </div>
    )
  }

  return (
    // Stack above the floating chat launcher (z-index 9998). Without this,
    // the fixed-position launcher overlaps the picker in the bottom of the
    // page footer and intercepts taps on both the trigger and the dropdown.
    <div className="relative z-[9999]">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="hover:text-aubergine-600 inline-flex cursor-pointer items-center gap-1.5 text-sm text-neutral-600 transition-colors"
      >
        <CountryFlag flag={countryConfig.flag} className="w-[14px]" />
        {localeLabels[locale] || "EN"}
        <svg
          width="10"
          height="10"
          viewBox="0 0 12 12"
          fill="none"
          aria-hidden="true"
          className={cn(
            "transition-transform duration-200",
            open && "rotate-180"
          )}
        >
          <path
            d="M3 4.5L6 7.5L9 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {/* Opens upward (footer sits at page bottom).
       * safe-animate-presence: conditional-mount — child only renders when `open=true` */}
      <AnimatePresence>
        {open && (
          <m.div
            initial={{ opacity: 0, y: 6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.98 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            style={{ transformOrigin: "bottom left" }}
            className="absolute bottom-full left-0 z-10 mb-2 w-[17rem] rounded-[16px] bg-white p-1.5 shadow-lg"
          >
            <LocaleGrid
              countries={pickerCountries}
              currentCountry={countryConfig.code}
              currentLocale={locale}
              onSelect={handleSelect}
              layout="stacked"
            />
          </m.div>
        )}
      </AnimatePresence>
    </div>
  )
}

/** Region/language grid for the mobile slide-in menu. Exported for
 *  `mobile-menu.tsx`, which owns the trigger + open state. */
export { LocaleGrid }
